import type {
  SystemStatus,
  HealthCheck,
  Device,
  DeviceFilters,
  DeviceUpdateRequest,
  SensorObservationsResponse,
  PersonasResponse,
  PersonaDetail,
  PersonaFilters,
  DetectionsResponse,
  DetectionFilters,
} from '../types';
import { auth } from '../config/firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

class APIClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const headers = await this.getHeaders();

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options?.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Health & Status
  async getHealth(): Promise<HealthCheck> {
    return this.request<HealthCheck>('/health');
  }

  async getSystemStatus(): Promise<SystemStatus> {
    return this.request<SystemStatus>('/status');
  }

  // Personas
  async getPersonas(filters?: PersonaFilters): Promise<PersonasResponse> {
    const params = new URLSearchParams();

    if (filters?.facility_id) params.append('facility_id', filters.facility_id);
    if (filters?.persona_status) params.append('persona_status', filters.persona_status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const query = params.toString();
    const endpoint = query ? `/api/v1/personas?${query}` : '/api/v1/personas';

    return this.request<PersonasResponse>(endpoint);
  }

  async getPersona(id: string): Promise<PersonaDetail> {
    return this.request<PersonaDetail>(`/api/v1/personas/${id}`);
  }

  // Detections
  async getDetections(filters?: DetectionFilters): Promise<DetectionsResponse> {
    const params = new URLSearchParams();

    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    if (filters?.persona_id) params.append('persona_id', filters.persona_id);
    if (filters?.facility_id) params.append('facility_id', filters.facility_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.processing_state) params.append('processing_state', filters.processing_state);

    const query = params.toString();
    const endpoint = query ? `/api/v1/detections?${query}` : '/api/v1/detections';

    return this.request<DetectionsResponse>(endpoint);
  }

  // Devices
  async getDevices(filters?: DeviceFilters): Promise<{ devices: Device[]; total: number }> {
    const params = new URLSearchParams();

    if (filters?.state) params.append('state', filters.state);
    if (filters?.facility_id) params.append('facility_id', filters.facility_id);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const query = params.toString();
    const endpoint = query ? `/api/v1/devices?${query}` : '/api/v1/devices';

    return this.request<{ devices: Device[]; total: number }>(endpoint);
  }

  async getDevice(sensorId: string): Promise<Device> {
    return this.request<Device>(`/api/v1/devices/${sensorId}`);
  }

  async updateDevice(sensorId: string, data: DeviceUpdateRequest): Promise<Device> {
    return this.request<Device>(`/api/v1/devices/${sensorId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async disableDevice(sensorId: string): Promise<{ status: string; sensor_id: string }> {
    return this.request<{ status: string; sensor_id: string }>(`/api/v1/devices/${sensorId}/disable`, {
      method: 'POST',
    });
  }

  async deleteDevice(sensorId: string): Promise<{ status: string; sensor_id: string }> {
    return this.request<{ status: string; sensor_id: string }>(`/api/v1/devices/${sensorId}`, {
      method: 'DELETE',
    });
  }

  async deleteDetection(observationId: number): Promise<{ status: string; observation_id: number; persona_deleted: boolean }> {
    return this.request<{ status: string; observation_id: number; persona_deleted: boolean }>(
      `/api/v1/detections/${observationId}`,
      { method: 'DELETE' }
    );
  }

  // Sensor Observations (simplified detections for activity feed)
  async getRecentObservations(limit: number = 50): Promise<SensorObservationsResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('processing_state', 'all');

    const endpoint = `/api/v1/detections?${params.toString()}`;

    // Map API response to simplified observations
    const response = await this.request<{
      detections: Array<{
        observation_id: number;
        sensor_id: string;
        timestamp: string;
        facility_id: string;
        location: { lat: number; lon: number } | null;
        wireless_signals: Array<any>;
        faces: Array<any>;
        plate: any | null;
      }>;
      count: number;
    }>(endpoint);

    return {
      observations: response.detections.map((d) => ({
        observation_id: d.observation_id,
        sensor_id: d.sensor_id,
        timestamp: d.timestamp,
        facility_id: d.facility_id,
        location: d.location,
        device_count: d.wireless_signals?.length || 0,
        face_count: d.faces?.length || 0,
        has_plate: d.plate !== null,
      })),
      count: response.count,
    };
  }
}

export const apiClient = new APIClient();
